<template>
  <div
    class="tool-card group"
    tabindex="0"
    role="button"
    :aria-label="`Execute ${tool.name}: ${tool.description}`"
    @click="$emit('execute', tool)"
    @keydown.enter="$emit('execute', tool)"
    @keydown.space.prevent="$emit('execute', tool)"
  >
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-center space-x-3">
          <!-- Tool Icon -->
          <div class="flex-shrink-0">
            <div
              class="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center"
            >
              <component
                :is="getToolIcon(tool.category)"
                class="w-5 h-5 text-primary-600 dark:text-primary-400"
              />
            </div>
          </div>

          <!-- Tool Info -->
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {{ formatToolName(tool.name) }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ tool.category }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center space-x-2">
          <!-- Favorite Button -->
          <button
            class="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :aria-label="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
            @click.stop="$emit('favorite', tool.name)"
          >
            <svg
              class="w-4 h-4 transition-colors duration-200"
              :class="
                isFavorite ? 'text-yellow-500 fill-current' : 'text-gray-400 hover:text-yellow-500'
              "
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>

          <!-- More Options -->
          <button
            class="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="More options"
            @click.stop="showOptions = !showOptions"
          >
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Description -->
      <div class="mt-3 flex-1">
        <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {{ tool.description }}
        </p>
      </div>

      <!-- Tags -->
      <div v-if="tool.tags && tool.tags.length > 0" class="mt-3">
        <div class="flex flex-wrap gap-1">
          <span
            v-for="tag in tool.tags.slice(0, 3)"
            :key="tag"
            class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
          >
            {{ tag }}
          </span>
          <span
            v-if="tool.tags.length > 3"
            class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
          >
            +{{ tool.tags.length - 3 }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-4 flex items-center justify-between">
        <!-- Status Indicators -->
        <div class="flex items-center space-x-2">
          <!-- Deprecated indicator -->
          <span
            v-if="tool.deprecated"
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200"
          >
            Deprecated
          </span>

          <!-- Version -->
          <span v-if="tool.version" class="text-xs text-gray-500 dark:text-gray-400">
            v{{ tool.version }}
          </span>
        </div>

        <!-- Execute Button -->
        <button
          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 dark:bg-primary-900/50 dark:text-primary-300 dark:hover:bg-primary-900/70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
          :disabled="tool.deprecated"
          @click.stop="$emit('execute', tool)"
        >
          <svg
            class="w-3 h-3 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1M9 18h6"
            />
          </svg>
          Execute
        </button>
      </div>

      <!-- Options Dropdown -->
      <div
        v-if="showOptions"
        class="absolute top-12 right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10"
        @click.stop
      >
        <button
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="viewDetails"
        >
          <svg
            class="w-4 h-4 mr-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          View Details
        </button>

        <button
          v-if="!tool.deprecated"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="quickExecute"
        >
          <svg
            class="w-4 h-4 mr-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          Quick Execute
        </button>

        <button
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="viewExamples"
        >
          <svg
            class="w-4 h-4 mr-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          View Examples
        </button>
      </div>
    </div>

    <!-- Hover overlay -->
    <div
      class="absolute inset-0 bg-primary-50 dark:bg-primary-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg pointer-events-none"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { McpTool } from '@/types/mcp';

export interface ToolCardProps {
  tool: McpTool;
  isFavorite?: boolean;
}

const props = withDefaults(defineProps<ToolCardProps>(), {
  isFavorite: false,
});

defineEmits<{
  execute: [tool: McpTool];
  favorite: [toolName: string];
}>();

const showOptions = ref(false);

// Methods
const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getToolIcon = (category: string) => {
  switch (category) {
    case 'search':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        `,
      };
    case 'calculation':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        `,
      };
    case 'communication':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        `,
      };
    case 'document':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        `,
      };
    case 'ai':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        `,
      };
    case 'utility':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        `,
      };
    default:
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        `,
      };
  }
};

const viewDetails = () => {
  showOptions.value = false;
  // Emit or navigate to tool details
  console.log('View details for', props.tool.name);
};

const quickExecute = () => {
  showOptions.value = false;
  // Quick execute with default parameters
  console.log('Quick execute', props.tool.name);
};

const viewExamples = () => {
  showOptions.value = false;
  // Show examples modal or navigate to examples
  console.log('View examples for', props.tool.name);
};

// Close options when clicking outside
const handleClickOutside = () => {
  showOptions.value = false;
};

// Add event listener for clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', handleClickOutside);
}
</script>

<style scoped>
.tool-card {
  @apply relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700;
  @apply hover:border-primary-300 dark:hover:border-primary-600;
  @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent;
  @apply transition-all duration-200 cursor-pointer;
  @apply transform hover:scale-[1.02] hover:shadow-lg;
  @apply p-4;
}

.tool-card:active {
  @apply transform scale-[0.98];
}

.tool-card[aria-disabled='true'] {
  @apply opacity-50 cursor-not-allowed;
  @apply transform-none hover:scale-100 hover:shadow-none;
}

/* Line clamp utility for description */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .tool-card {
    @apply transform-none;
    transition: none;
  }

  .tool-card:hover {
    @apply transform-none;
  }

  .tool-card:active {
    @apply transform-none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .tool-card {
    @apply border-2;
  }

  .tool-card:focus {
    @apply ring-4;
  }
}

/* Print styles */
@media print {
  .tool-card {
    @apply transform-none shadow-none;
    break-inside: avoid;
  }
}
</style>
